import React, { useEffect } from 'react';
import { Event } from '../../../types/event.types';
import { useSmartSeries } from '../../../hooks/useSmartSeries';
import { formatEventDateTime } from '../../../utils/timezoneUtils';
import { Icon, Button, ContentBlock } from '../../../pages/DesignSystem';
import { optimizeCloudinaryUrl } from '../../../services/cloudinary.service';
import EventCardKPIs from './EventCardKPIs';
import EventCardMenu from './EventCardMenu';
import ConfidenceBadge from '../EventImport/ConfidenceBadge';
import { useOrganizerEventsStore, useSeriesMetrics } from '../../../stores/organizerEventsStore';
import './EventList.css';

interface RecurringEventDetailsProps {
  mainEvent: Event;
  seriesEvents: Event[];
  activeTab?: 'active' | 'draft' | 'past';
  onEditSeries: (event: Event) => void;
  onEditOccurrence: (event: Event) => void;
  onStatusChange?: (event: Event, newStatus: 'draft' | 'active') => void;
  onCancelOccurrence?: (event: Event) => void;
  onRestoreOccurrence?: (event: Event) => void;
  onDuplicateSeries?: (event: Event) => void;
  onCancelSeries?: (event: Event) => void;
  onRestoreSeries?: (event: Event) => void;
  onPublishSeries?: (event: Event) => void;
  onDeleteSeries?: (event: Event) => void;
  onAddOccurrence?: (event: Event) => void;
  onInstagram?: (event: Event) => void;
  isExpanded: boolean;
  onToggleExpand: (expanded: boolean) => void;
}

const RecurringEventDetails: React.FC<RecurringEventDetailsProps> = ({
  mainEvent,
  seriesEvents,
  activeTab,
  onEditSeries,
  onEditOccurrence,
  onCancelOccurrence,
  onRestoreOccurrence,
  onDuplicateSeries,
  onCancelSeries,
  onRestoreSeries,
  onPublishSeries,
  onDeleteSeries,
  onAddOccurrence,
  onInstagram,
  isExpanded,
  onToggleExpand
}) => {
  const { series, pattern, loading } = useSmartSeries(mainEvent.seriesId, undefined, mainEvent.organizerId);

  // Series metrics
  const seriesMetrics = useSeriesMetrics(mainEvent.seriesId || '');
  const loadSeriesMetrics = useOrganizerEventsStore(state => state.loadSeriesMetrics);
  const metricsLoading = useOrganizerEventsStore(state => state.metricsLoading);

  // Load series metrics on mount if seriesId exists
  useEffect(() => {
    if (mainEvent.seriesId && !seriesMetrics) {
      loadSeriesMetrics(mainEvent.seriesId);
    }
  }, [mainEvent.seriesId, seriesMetrics, loadSeriesMetrics]);

  const sortedEvents = [...seriesEvents].sort((a, b) => {
    const dateA = a.datetime ? a.datetime.getTime() : 0;
    const dateB = b.datetime ? b.datetime.getTime() : 0;
    return dateA - dateB;
  });

  // Fallback pattern with date range (two lines)
  const fallbackPattern = (() => {
    if (sortedEvents.length === 0) return <span>Recurring event</span>;
    const firstDate = sortedEvents[0]?.datetime;
    const lastDate = sortedEvents[sortedEvents.length - 1]?.datetime;

    if (firstDate && lastDate) {
      const firstStr = formatEventDateTime(firstDate, mainEvent.timezone).date;
      const lastStr = formatEventDateTime(lastDate, mainEvent.timezone).date;
      return (
        <>
          <span>{seriesEvents.length} dates</span>
          <span className="organizer-card__date-range">{firstStr} → {lastStr}</span>
        </>
      );
    }
    return <span>Recurring event ({seriesEvents.length} dates)</span>;
  })();

  return (
    <>
      {/* Header Sticky */}
      <div className="organizer-card__header organizer-card__header--sticky">
        {mainEvent.image && (
          <div className="organizer-card__image">
            <img
              src={optimizeCloudinaryUrl(mainEvent.image, { width: 200 })}
              alt={mainEvent.title}
              className="organizer-card__image-content"
            />
          </div>
        )}

        <div className="organizer-card__content">
          {/* Row 1: Title with optional CANCELLED badge */}
          <h3 className="organizer-card__title">
            {mainEvent.status === 'cancelled' && (
              <span className="organizer-card__cancelled-badge">CANCELLED</span>
            )}
            {mainEvent.title}
          </h3>
          {/* Row 2: Pattern + Kebab menu */}
          <div className="organizer-card__row">
            <p className="organizer-card__recurrence-pattern">
              {(loading && !pattern) ? (
                <span
                  className="skeleton skeleton--text"
                  style={{
                    display: 'inline-block',
                    height: '1rem',
                    width: '85%'
                  }}
                />
              ) : (
                pattern || fallbackPattern
              )}
            </p>
            <div className="organizer-card__action-slot" onClick={(e) => e.stopPropagation()}>
              {mainEvent.status === 'active' && activeTab === 'active' && onInstagram && (
                <button
                  className="organizer-card__insta-btn"
                  onClick={() => onInstagram(mainEvent)}
                  title="Generate Instagram post"
                >
                  <Icon name="Instagram" size={18} />
                </button>
              )}
              <EventCardMenu
                eventId={mainEvent.id}
                eventStatus={mainEvent.status as 'active' | 'draft' | 'cancelled'}
                activeTab={activeTab}
                isSeriesMenu={true}
                onEdit={() => onEditSeries(mainEvent)}
                onDuplicate={() => onDuplicateSeries?.(mainEvent)}
                onCancel={() => onCancelSeries?.(mainEvent)}
                onRestore={() => onRestoreSeries?.(mainEvent)}
                onPublish={() => onPublishSeries?.(mainEvent)}
                onDelete={() => onDeleteSeries?.(mainEvent)}
                onAddOccurrence={() => onAddOccurrence?.(mainEvent)}
                onInstagram={() => onInstagram?.(mainEvent)}
              />
            </div>
          </div>
          {/* Row 3: Location */}
          <p className="organizer-card__location">
            {mainEvent.locationName || mainEvent.location || 'Lieu non défini'}
          </p>
          {/* Row 4: KPIs + Chevron */}
          <div className="organizer-card__row">
            <EventCardKPIs
              metrics={seriesMetrics || null}
              isLoading={metricsLoading}
              variant="compact"
              isSeries={true}
            />
            <div className="organizer-card__action-slot" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="organizer-card__chevron-button"
                onClick={() => onToggleExpand(!isExpanded)}
              >
                <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confidence badge for imported series */}
      {mainEvent.status === 'draft' && series?.importConfidence != null && (
        <div className="organizer-card__confidence">
          <ConfidenceBadge confidence={series.importConfidence} />
        </div>
      )}

      {/* Contenu défilable */}
      <div className={`organizer-occurrences__content ${isExpanded ? 'organizer-occurrences__content--expanded' : ''}`}>
        {isExpanded && (
          <ContentBlock 
            title="All dates"
            padding="md"
            scrollable={true}
            maxHeight="400px"
            className="organizer-occurrences__content-block"
          >
            <div className="organizer-occurrences__dates-list">
              {sortedEvents.map((event, index) => (
                <div
                  key={event.id}
                  className={`organizer-occurrence ${event.status === 'cancelled' ? 'organizer-occurrence--cancelled' : ''}`}
                  style={{ animationDelay: `${(index + 1) * 0.05}s` }}
                >
                  <div className="organizer-occurrence__info">
                    <div className="organizer-occurrence__date">
                      <span className="organizer-occurrence__date-value">
                        {formatEventDateTime(event.datetime, event.timezone).date}
                      </span>
                      <span className="organizer-occurrence__time">
                        {formatEventDateTime(event.datetime, event.timezone).time}
                      </span>
                    </div>
                    {event.status === 'cancelled' && (
                      <div className="organizer-occurrence__status organizer-occurrence__status--cancelled">
                        <Icon name="XCircle" size={14} />
                        <span className="organizer-occurrence__status-text">Cancelled</span>
                      </div>
                    )}
                    {event.isException && event.status !== 'cancelled' && (
                      <div className="organizer-occurrence__status">
                        <Icon name="AlertCircle" size={14} />
                        <span className="organizer-occurrence__status-text">Modified</span>
                      </div>
                    )}
                  </div>
                  <div className="organizer-occurrence__actions">
                    {event.status === 'cancelled' ? (
                      onRestoreOccurrence && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRestoreOccurrence(event);
                          }}
                        >
                          <Icon name="RotateCcw" size={14} />
                          Restore
                        </Button>
                      )
                    ) : (
                      onCancelOccurrence && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelOccurrence(event);
                          }}
                        >
                          <Icon name="XCircle" size={14} />
                          Cancel
                        </Button>
                      )
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditOccurrence(event);
                      }}
                    >
                      <Icon name="Edit" size={14} />
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ContentBlock>
        )}
      </div>
    </>
  );
};

export default RecurringEventDetails;
